import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../api/client'
import { describeError } from '../api/errors'
import { reportRequestFailure, useServerStatus } from '../api/serverStatus'
import type { AudioTrack, Line, ProjectFull, Role, Take } from '../api/types'
import { PlayResult } from '../components/play/PlayResult'
import { PlayRun } from '../components/play/PlayRun'
import { PlaySetup } from '../components/play/PlaySetup'
import { activeLineIds, buildPlaylist, countRecorded, roleProgress } from '../game/order'
import type { PlayItem } from '../game/order'
import {
  readAutoPlayTake,
  readHandoffBetweenRoles,
  readListenOriginal,
  readOrder,
  writeAutoPlayTake,
  writeHandoffBetweenRoles,
  writeListenOriginal,
  writeOrder,
} from '../game/settings'
import type { PlayOrder } from '../game/settings'
import { navigate } from '../lib/router'
import type { PlayStage } from '../lib/router'
import { findTrackByKind } from '../lib/tracks'

export type PlayPageProps = { projectId: string; stage: PlayStage }

// Стабильные пустышки: иначе useMemo пересчитывался бы каждый рендер.
const NO_LINES: Line[] = []
const NO_ROLES: Role[] = []
const NO_TAKES: Take[] = []

/**
 * Игровой режим (docs/GAME.md). Ни одного нового эндпоинта: прогресс выводится
 * из активных дублей, запись идёт в POST /api/lines/{id}/takes, сведение —
 * в POST /api/projects/{id}/render.
 */
export function PlayPage({ projectId, stage }: PlayPageProps): JSX.Element {
  const status = useServerStatus()
  const [data, setData] = useState<ProjectFull | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tracks, setTracks] = useState<AudioTrack[] | null>(null)
  const [order, setOrder] = useState<PlayOrder>(readOrder)
  const [listenOriginal, setListenOriginal] = useState<boolean>(readListenOriginal)
  const [autoPlayTake, setAutoPlayTake] = useState<boolean>(readAutoPlayTake)
  const [handoffBetweenRoles, setHandoffBetweenRoles] = useState<boolean>(readHandoffBetweenRoles)
  /**
   * Плейлист забега фиксируется на старте. Пересобирать его на каждый новый
   * дубль нельзя: индекс текущей реплики немедленно разъехался бы.
   */
  const [plan, setPlan] = useState<PlayItem[] | null>(null)
  const [runKey, setRunKey] = useState(0)

  const load = useCallback(async (): Promise<void> => {
    try {
      const full = await api.getProject(projectId)
      setData(full)
      if (full.tracks) setTracks(full.tracks)
      setLoadError(null)
    } catch (err) {
      reportRequestFailure(err)
      setLoadError(describeError(err))
    }
  }, [projectId])

  useEffect(() => {
    setData(null)
    setTracks(null)
    setPlan(null)
    void load()
  }, [load])

  useEffect(() => {
    if (status.online === true && loadError !== null && data === null) void load()
  }, [status.online, loadError, data, load])

  const project = data?.project ?? null
  const projectMode = project?.mode ?? 'mute'

  // Дорожки нужны только режиму replace — в остальных случаях лишний запрос.
  useEffect(() => {
    if (projectMode !== 'replace' || tracks !== null) return
    let cancelled = false
    void api.listTracks(projectId).then(
      (list) => {
        if (!cancelled) setTracks(Array.isArray(list) ? list : [])
      },
      () => {
        if (!cancelled) setTracks([])
      },
    )
    return () => {
      cancelled = true
    }
  }, [projectMode, projectId, tracks])

  const lines = data?.lines ?? NO_LINES
  const roles = data?.roles ?? NO_ROLES
  const takes = data?.takes ?? NO_TAKES

  const recordedIds = useMemo(() => activeLineIds(takes), [takes])
  const items = useMemo(
    () => buildPlaylist(lines, roles, order, handoffBetweenRoles),
    [lines, roles, order, handoffBetweenRoles],
  )
  const roleStats = useMemo(() => roleProgress(items, recordedIds), [items, recordedIds])
  const recordedCount = useMemo(() => countRecorded(items, recordedIds), [items, recordedIds])

  // Прямой заход на #/…/play/run (перезагрузка страницы) — восстанавливаем забег.
  useEffect(() => {
    if (stage !== 'run' || plan !== null) return
    if (!data || items.length === 0) return
    setPlan(items)
  }, [stage, plan, data, items])

  const goEditor = useCallback((): void => {
    navigate({ name: 'editor', projectId })
  }, [projectId])

  const goStage = useCallback(
    (next: PlayStage): void => {
      navigate({ name: 'play', projectId, stage: next })
    },
    [projectId],
  )

  const handleRecorded = useCallback((take: Take | null): void => {
    if (!take) return
    setData((prev) => (prev ? { ...prev, takes: applyTake(prev.takes, take) } : prev))
  }, [])

  const startGame = (): void => {
    setPlan(items)
    setRunKey((key) => key + 1)
    goStage('run')
  }

  const replayMissing = (): void => {
    const missing = lines.filter((line) => !recordedIds.has(line.id))
    const next = buildPlaylist(missing, roles, order, handoffBetweenRoles)
    if (next.length === 0) return
    setPlan(next)
    setRunKey((key) => key + 1)
    goStage('run')
  }

  const restart = (): void => {
    setPlan(null)
    goStage('setup')
  }

  const changeOrder = (next: PlayOrder): void => {
    setOrder(next)
    writeOrder(next)
  }

  const changeListen = (next: boolean): void => {
    setListenOriginal(next)
    writeListenOriginal(next)
  }

  const changeAutoPlay = (next: boolean): void => {
    setAutoPlayTake(next)
    writeAutoPlayTake(next)
  }

  const changeHandoff = (next: boolean): void => {
    setHandoffBetweenRoles(next)
    writeHandoffBetweenRoles(next)
  }

  // ---------------------------------------------------------------- вывод

  if (loadError && !data) {
    return (
      <div className="play-page">
        <div className="empty">
          <p>Не удалось открыть проект.</p>
          <p className="faint">{loadError}</p>
          <div className="row" style={{ justifyContent: 'center' }}>
            <button type="button" onClick={() => void load()}>
              Повторить
            </button>
            <button type="button" className="ghost" onClick={goEditor}>
              В редактор
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="play-page">
        <div className="empty">Загружаю проект…</div>
      </div>
    )
  }

  const videoSrc = project.videoFile ? api.videoUrl(projectId) : null

  if (stage === 'result') {
    return (
      <PlayResult
        projectId={projectId}
        title={project.title}
        mode={projectMode}
        items={items}
        recordedIds={recordedIds}
        roles={roleStats}
        ffmpegAvailable={status.ffmpeg}
        meMissing={projectMode === 'replace' && findTrackByKind(tracks ?? [], 'me') === null}
        onReplayMissing={replayMissing}
        onRestart={restart}
        onExit={goEditor}
      />
    )
  }

  if (stage === 'run') {
    if (!videoSrc || items.length === 0) {
      return (
        <div className="play-page">
          <div className="empty">
            <p>Играть пока не во что.</p>
            <p className="faint">
              {videoSrc ? 'В проекте нет реплик.' : 'В проекте нет видео.'} Вернитесь к настройкам
              игры — там написано, чего не хватает.
            </p>
            <button type="button" className="primary" onClick={() => goStage('setup')}>
              К настройкам игры
            </button>
          </div>
        </div>
      )
    }
    if (!plan) {
      return (
        <div className="play-page">
          <div className="empty">Готовлю реплики…</div>
        </div>
      )
    }
    return (
      <PlayRun
        key={runKey}
        items={plan}
        videoSrc={videoSrc}
        recordedIds={recordedIds}
        listenOriginal={listenOriginal}
        autoPlayTake={autoPlayTake}
        onRecorded={handleRecorded}
        onExit={goEditor}
        onFinish={() => goStage('result')}
      />
    )
  }

  return (
    <PlaySetup
      title={project.title}
      hasVideo={videoSrc !== null}
      items={items}
      recordedCount={recordedCount}
      roles={roleStats}
      order={order}
      listenOriginal={listenOriginal}
      autoPlayTake={autoPlayTake}
      handoffBetweenRoles={handoffBetweenRoles}
      onOrderChange={changeOrder}
      onListenChange={changeListen}
      onAutoPlayChange={changeAutoPlay}
      onHandoffChange={changeHandoff}
      onStart={startGame}
      onExit={goEditor}
    />
  )
}

/** Новый дубль автоматически активен, предыдущий у той же реплики — нет. */
function applyTake(takes: Take[], next: Take): Take[] {
  const cleared = takes.map((take) =>
    take.lineId === next.lineId && next.isActive && take.id !== next.id
      ? { ...take, isActive: false }
      : take,
  )
  return cleared.some((take) => take.id === next.id)
    ? cleared.map((take) => (take.id === next.id ? next : take))
    : [...cleared, next]
}
